import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useBatchTiming } from "../hooks/useBatchTiming";
import { useCourse } from "../../course/hooks/useCourse";
import { showToast } from "../../../../core/utils/alert";
import { all_routes } from "../../../router/all_routes";
import { BatchTimingPayloadType } from "../types/batchTimingRequest";
import { CourseResponseType } from "../../course/types/courseResponse";
import { useGetBatchTimingByIdQuery } from "../api/batchTimingApi";
import { useCenter } from "../../../peoples/centers/hooks/useCenter";
import { useSelector } from "react-redux";
import {
  selectCurrentUser,
  selectUserRole,
} from "../../../../core/data/slice/authSlice";
import { useForm, Controller, useFormContext } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

interface FormData {
  courseId: string;
  centerId: string;
  stageId: string;
  name: string;
  startTime: string;
  endTime: string;
  status: string;
  days: string[]; // Added for UI only
}

const DAYS_OF_WEEK = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const AddBatchTiming: React.FC = () => {
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const navigate = useNavigate();
  const { addBatchTiming, updateBatchTiming } = useBatchTiming();
  const { data: batchData, isLoading: isLoadingBatch } =
    useGetBatchTimingByIdQuery({ id: id || "" }, { skip: !id });
  const { getCourseState } = useCourse();
  const { courses, isLoading: isLoadingCourses } = getCourseState();
  const userRole = useSelector(selectUserRole);
  const currentUser = useSelector(selectCurrentUser);
  const isSuperAdmin = userRole === "superadmin";
  const { getCenterState } = useCenter({
    page: 1,
    size: 100,
    skip: userRole !== "superadmin",
  });
  const { centers, isLoading: isLoadingCenters } = isSuperAdmin
    ? getCenterState()
    : { centers: [], isLoading: false };

  // Define validation schema
  const validationSchema = yup.object().shape({
    courseId: yup.string().required("Course is required"),
    centerId: yup.string().required("Center is required"),
    stageId: yup.string().required("Stage is required"),
    name: yup.string().required("Batch name is required"),
    startTime: yup.string().required("Start time is required"),
    endTime: yup.string().required("End time is required"),
    status: yup.string().required("Status is required"),
    days: yup.array().min(1, "At least one day must be selected"),
  });

  // Initialize react-hook-form
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    watch,
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      courseId: "",
      centerId: isSuperAdmin ? "" : currentUser?.center_id?.toString() || "",
      stageId: "",
      name: "",
      startTime: "",
      endTime: "",
      status: "active",
      days: [],
    },
  });

  // Load initial data when in edit mode
  useEffect(() => {
    if (isEditMode && batchData?.data?.batch) {
      const batch = batchData.data.batch;

      // Convert time format if needed (e.g., "10:30am" to "10:30")
      const formatTime = (timeStr: string) => {
        try {
          // Remove 'am' or 'pm' and convert to 24-hour format
          const [time, meridian] =
            timeStr.match(/(\d+:\d+)([ap]m)/i)?.slice(1) || [];
          if (!time || !meridian) return timeStr;

          let [hours, minutes] = time.split(":");
          let hoursNum = parseInt(hours);

          // Convert to 24-hour format
          if (meridian.toLowerCase() === "pm" && hoursNum !== 12) {
            hoursNum += 12;
          } else if (meridian.toLowerCase() === "am" && hoursNum === 12) {
            hoursNum = 0;
          }

          return `${hoursNum.toString().padStart(2, "0")}:${minutes}`;
        } catch (error) {
          return timeStr;
        }
      };

      setValue("courseId", batch.course_id.toString());
      setValue("centerId", batch.center_id.toString());
      setValue("stageId", batch.stage_id.toString());
      setValue("name", batch.name);
      setValue("startTime", formatTime(batch.start_time));
      setValue("endTime", formatTime(batch.end_time));
      setValue("status", batch.status);
      setValue("days", batch.days || []);
    }
  }, [isEditMode, batchData, setValue]);

  const watchedCourseId = watch("courseId");

  const selectedCourse = courses.find(
    (course: CourseResponseType) => course.id.toString() === watchedCourseId
  );

  const watchedDays = watch("days");

  const onSubmit = async (data: any) => {
    const payload: BatchTimingPayloadType = {
      course_id: parseInt(data.courseId),
      center_id: isSuperAdmin
        ? parseInt(data.centerId)
        : currentUser?.center_id || 0,
      stage_id: parseInt(data.stageId),
      name: data.name,
      start_time: data.startTime,
      end_time: data.endTime,
      status: data.status,
      days: data.days,
    };

    try {
      if (isEditMode && id) {
        await updateBatchTiming(id, payload);
      } else {
        await addBatchTiming(payload);
      }
      navigate(all_routes.batchTimingList);
    } catch (error) {
      showToast({
        title: "Error",
        text: "Failed to save batch timing",
        icon: "error",
      });
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content content-two">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="mb-1">{isEditMode ? "Edit" : "Add"} Batch Timing</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to="/">Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={all_routes.batchTimingList}>Batch Timings</Link>
                </li>
                <li className="breadcrumb-item active">
                  {isEditMode ? "Edit" : "Add"} Batch Timing
                </li>
              </ol>
            </nav>
          </div>
        </div>

        <div className="row">
          <div className="col-md-12">
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="card">
                <div className="card-header bg-light">
                  <div className="d-flex align-items-center">
                    <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                      <i className="ti ti-clock fs-16" />
                    </span>
                    <h4 className="text-dark">Batch Timing Details</h4>
                  </div>
                </div>
                <div className="card-body pb-1">
                  <div className="row">
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">Course <span className="text-danger">*</span></label>
                        <Controller
                          name="courseId"
                          control={control}
                          render={({ field }) => (
                            <select
                              {...field}
                              className={`form-control ${errors.courseId ? 'is-invalid' : ''}`}
                            >
                              <option value="">Select Course</option>
                              {courses.map((course: CourseResponseType) => (
                                <option key={course.id} value={course.id}>
                                  {course.name}
                                </option>
                              ))}
                            </select>
                          )}
                        />
                        {errors.courseId && (
                          <div className="invalid-feedback">{errors.courseId.message}</div>
                        )}
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">Level <span className="text-danger">*</span></label>
                        <Controller
                          name="stageId"
                          control={control}
                          render={({ field }) => (
                            <select
                              {...field}
                              className={`form-control ${errors.stageId ? 'is-invalid' : ''}`}
                              disabled={!selectedCourse}
                            >
                              <option value="">Select Level</option>
                              {selectedCourse?.courseStages?.map((stage) => (
                                <option key={stage.id} value={stage.id}>
                                  {stage.name}
                                </option>
                              ))}
                            </select>
                          )}
                        />
                        {errors.stageId && (
                          <div className="invalid-feedback">{errors.stageId.message}</div>
                        )}
                      </div>
                    </div>
                    {isSuperAdmin ? (
                      <div className="col-lg-6">
                        <div className="mb-3">
                          <label className="form-label">Center <span className="text-danger">*</span></label>
                          <Controller
                            name="centerId"
                            control={control}
                            render={({ field }) => (
                              <select
                                {...field}
                                className={`form-control ${errors.centerId ? 'is-invalid' : ''}`}
                              >
                                <option value="">Select Center</option>
                                {centers.map((center) => (
                                  <option key={center.id} value={center.id}>
                                    {center.name}
                                  </option>
                                ))}
                              </select>
                            )}
                          />
                          {errors.centerId && (
                            <div className="invalid-feedback">{errors.centerId.message}</div>
                          )}
                        </div>
                      </div>
                    ) : null}
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">Batch Name <span className="text-danger">*</span></label>
                        <Controller
                          name="name"
                          control={control}
                          render={({ field }) => (
                            <input
                              type="text"
                              className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                              placeholder="Enter batch timing name"
                              {...field}
                            />
                          )}
                        />
                        {errors.name && (
                          <div className="invalid-feedback">{errors.name.message}</div>
                        )}
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">Status <span className="text-danger">*</span></label>
                        <Controller
                          name="status"
                          control={control}
                          render={({ field }) => (
                            <select
                              {...field}
                              className={`form-select ${errors.status ? 'is-invalid' : ''}`}
                            >
                              <option value="active">Active</option>
                              <option value="inactive">Inactive</option>
                            </select>
                          )}
                        />
                        {errors.status && (
                          <div className="invalid-feedback">{errors.status.message}</div>
                        )}
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">Start Time <span className="text-danger">*</span></label>
                        <Controller
                          name="startTime"
                          control={control}
                          render={({ field }) => (
                            <input
                              type="time"
                              className={`form-control ${errors.startTime ? 'is-invalid' : ''}`}
                              {...field}
                            />
                          )}
                        />
                        {errors.startTime && (
                          <div className="invalid-feedback">{errors.startTime.message}</div>
                        )}
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">End Time <span className="text-danger">*</span></label>
                        <Controller
                          name="endTime"
                          control={control}
                          render={({ field }) => (
                            <input
                              type="time"
                              className={`form-control ${errors.endTime ? 'is-invalid' : ''}`}
                              {...field}
                            />
                          )}
                        />
                        {errors.endTime && (
                          <div className="invalid-feedback">{errors.endTime.message}</div>
                        )}
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="mb-3">
                        <label className="form-label">Days <span className="text-danger">*</span></label>
                        <div className="d-flex flex-wrap gap-3">
                          {DAYS_OF_WEEK.map((day) => (
                            <div key={day} className="form-check">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id={`day-${day}`}
                                checked={watchedDays?.includes(day)}
                                onChange={() => {
                                  const newDays = watchedDays?.includes(day)
                                    ? watchedDays.filter((d: string) => d !== day)
                                    : [...(watchedDays || []), day];
                                  setValue("days", newDays);
                                }}
                              />

                              <label
                                className="form-check-label"
                                htmlFor={`day-${day}`}
                              >
                                {day}
                              </label>
                            </div>
                          ))}
                        </div>
                        {errors.days && (
                          <div className="invalid-feedback d-block">{errors.days.message}</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="text-end mt-4">
                <Link
                  to={all_routes.batchTimingList}
                  className="btn btn-light me-3"
                >
                  Cancel
                </Link>
                <button type="submit" className="btn btn-primary">
                  {isEditMode ? "Update" : "Add"} Batch Timing
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddBatchTiming;
